import { getAccessToken, useAuthStore } from '@host/auth'
import {
  connectSocket,
  disconnectSocket,
  disposeVoiceManager,
  useSocketEvent,
} from '@host/realtime'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, type ReactNode } from 'react'

import { SOCKET_PATH, SOCKET_URL } from '@/constants'
import { useAppUiStore } from '@/shared/stores/appUiStore'

/**
 * Мост «сокет → остальное приложение».
 *
 * Без него реалтайм и запросы разъезжаются: пришло событие о новом посте,
 * а лента показывает закэшированный ответ и обновится только через полминуты.
 * Счётчики непрочитанного идут в UI-стор напрямую — они не запрашиваются,
 * а только приходят.
 */
function RealtimeBridge() {
  const queryClient = useQueryClient()
  const setSpaceUnread = useAppUiStore((state) => state.setSpaceUnread)

  useSocketEvent('post:new', () => {
    void queryClient.invalidateQueries({ queryKey: ['feed'] })
  })

  useSocketEvent('post:liked', () => {
    void queryClient.invalidateQueries({ queryKey: ['feed'] })
  })

  useSocketEvent('space:unread', ({ spaceId, unreadCount }) => {
    setSpaceUnread(spaceId, unreadCount)
  })

  useSocketEvent('presence:update', () => {
    void queryClient.invalidateQueries({ queryKey: ['presence'] })
  })

  return null
}

/**
 * Одно соединение на приложение, поднимается после входа.
 *
 * До авторизации сокет не нужен: сервер всё равно отвергнет подключение
 * без токена, а клиент начнёт цикл переподключений на экране входа.
 *
 * Отдельного провайдера под mediasoup нет намеренно. Устройство и транспорты
 * создаются лениво, при первом входе в голосовой канал, — вешать ради этого
 * пустой провайдер в дерево незачем. Здесь только уборка: когда соединение
 * уходит, комната обязана уйти вместе с ним, иначе останутся живые транспорты
 * на мёртвом сокете.
 */
export function SocketProvider({ children }: { children: ReactNode }) {
  const status = useAuthStore((state) => state.status)
  const connected = status === 'authenticated' && SOCKET_URL !== ''

  useEffect(() => {
    if (!connected) return

    connectSocket({ url: SOCKET_URL, path: SOCKET_PATH, getToken: getAccessToken })

    return () => {
      disposeVoiceManager()
      disconnectSocket()
    }
  }, [connected])

  return (
    <>
      {connected ? <RealtimeBridge /> : null}
      {children}
    </>
  )
}
